import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { AuthenticatedUser } from '@dcc/shared';
import { api, ApiRequestError } from './api';

interface AuthState {
  user: AuthenticatedUser | null;
  status: 'checking' | 'signed-in' | 'signed-out';
  signIn(email: string, password: string): Promise<void>;
  signOut(): Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthenticatedUser | null>(null);
  const [status, setStatus] = useState<AuthState['status']>('checking');

  useEffect(() => {
    let cancelled = false;
    api
      .me()
      .then((response) => {
        if (cancelled) return;
        setUser(response.user);
        setStatus('signed-in');
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        // A 401 here is the normal "not signed in yet" case, not a failure.
        if (!(error instanceof ApiRequestError) || error.status !== 401) {
          console.warn('Could not restore the session.');
        }
        setStatus('signed-out');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    const response = await api.login(email, password);
    setUser(response.user);
    setStatus('signed-in');
  }, []);

  const signOut = useCallback(async () => {
    await api.logout();
    setUser(null);
    setStatus('signed-out');
  }, []);

  const value = useMemo<AuthState>(
    () => ({ user, status, signIn, signOut }),
    [user, status, signIn, signOut],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside AuthProvider');
  return context;
}
